import React, { createContext, useContext, useEffect, useCallback, useState } from 'react';
import { CHAIN, brcToWeiHex, weiHexToBrc } from './config';

const WalletContext = createContext(null);

const getEthereum = () => (typeof window !== 'undefined' ? window.ethereum : undefined);

export function WalletProvider({ children }) {
    const [address, setAddress] = useState(null);
    const [balance, setBalance] = useState(null);
    const [chainOk, setChainOk] = useState(false);
    const [connecting, setConnecting] = useState(false);

    const hasWallet = !!getEthereum();

    const refreshBalance = useCallback(async (addr) => {
        const eth = getEthereum();
        if (!eth || !addr) return;
        try {
            const hex = await eth.request({ method: 'eth_getBalance', params: [addr, 'latest'] });
            setBalance(weiHexToBrc(hex));
        } catch (e) {
            setBalance(null);
        }
    }, []);

    const ensureChain = useCallback(async () => {
        const eth = getEthereum();
        if (!eth) return false;
        try {
            await eth.request({
                method: 'wallet_switchEthereumChain',
                params: [{ chainId: CHAIN.chainIdHex }],
            });
            setChainOk(true);
            return true;
        } catch (err) {
            // 4902: chain not added to the wallet yet
            if (err && (err.code === 4902 || String(err.message || '').includes('4902'))) {
                try {
                    await eth.request({
                        method: 'wallet_addEthereumChain',
                        params: [{
                            chainId: CHAIN.chainIdHex,
                            chainName: CHAIN.name,
                            rpcUrls: [CHAIN.rpcUrl],
                            blockExplorerUrls: [CHAIN.explorerUrl],
                            nativeCurrency: CHAIN.nativeCurrency,
                        }],
                    });
                    setChainOk(true);
                    return true;
                } catch (addErr) {
                    setChainOk(false);
                    return false;
                }
            }
            setChainOk(false);
            return false;
        }
    }, []);

    const connect = useCallback(async () => {
        const eth = getEthereum();
        if (!eth) {
            window.open('https://birr.foundation/wallet', '_blank');
            return null;
        }
        setConnecting(true);
        try {
            const accounts = await eth.request({ method: 'eth_requestAccounts' });
            const addr = accounts && accounts[0];
            if (!addr) return null;
            setAddress(addr);
            localStorage.setItem('bp_wallet_connected', '1');
            await ensureChain();
            refreshBalance(addr);
            return addr;
        } finally {
            setConnecting(false);
        }
    }, [ensureChain, refreshBalance]);

    const disconnect = useCallback(() => {
        setAddress(null);
        setBalance(null);
        localStorage.removeItem('bp_wallet_connected');
    }, []);

    // Send a native BRC transfer; resolves to the tx hash.
    const payBRC = useCallback(async (to, amountBRC) => {
        const eth = getEthereum();
        if (!eth || !address) throw new Error('Wallet not connected');
        const ok = await ensureChain();
        if (!ok) throw new Error('Please switch your wallet to ' + CHAIN.name);
        const txHash = await eth.request({
            method: 'eth_sendTransaction',
            params: [{ from: address, to, value: brcToWeiHex(amountBRC) }],
        });
        refreshBalance(address);
        return txHash;
    }, [address, ensureChain, refreshBalance]);

    // Silent re-connect + wallet event wiring
    useEffect(() => {
        const eth = getEthereum();
        if (!eth) return;
        if (localStorage.getItem('bp_wallet_connected')) {
            eth.request({ method: 'eth_accounts' }).then((accounts) => {
                if (accounts && accounts[0]) {
                    setAddress(accounts[0]);
                    refreshBalance(accounts[0]);
                    eth.request({ method: 'eth_chainId' })
                        .then((id) => setChainOk(id === CHAIN.chainIdHex))
                        .catch(() => {});
                }
            }).catch(() => {});
        }
        const onAccounts = (accounts) => {
            const addr = accounts && accounts[0];
            if (addr) { setAddress(addr); refreshBalance(addr); }
            else { setAddress(null); setBalance(null); }
        };
        const onChain = (id) => setChainOk(id === CHAIN.chainIdHex);
        if (eth.on) {
            eth.on('accountsChanged', onAccounts);
            eth.on('chainChanged', onChain);
        }
        return () => {
            if (eth.removeListener) {
                eth.removeListener('accountsChanged', onAccounts);
                eth.removeListener('chainChanged', onChain);
            }
        };
    }, [refreshBalance]);

    const value = { address, balance, chainOk, connecting, hasWallet, connect, disconnect, payBRC, refreshBalance, ensureChain };
    return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}

export const useWallet = () => {
    const ctx = useContext(WalletContext);
    if (!ctx) throw new Error('useWallet must be used inside <WalletProvider>');
    return ctx;
};
